import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnalysisAPI, OdkAPI, type PoststratifyResult } from "../api/endpoints";
import ProjectFormPicker from "../components/ProjectFormPicker";
import VariablePicker from "../components/VariablePicker";
import { Card, PageHeader, Spinner, ErrorBanner } from "../components/Layout";

export default function WeightingPage() {
  const [projectId, setProjectId] = useState<number | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [strataVar, setStrataVar] = useState<string>("");
  const [targetVar, setTargetVar] = useState<string>("");
  const [pop, setPop] = useState<Record<string, string>>({});
  const [result, setResult] = useState<PoststratifyResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Cargar el estrato para descubrir sus categorías (de la muestra).
  const strataQ = useQuery({
    queryKey: ["dict", projectId, formId],
    queryFn: () => OdkAPI.variables(projectId!, formId!),
    enabled: !!projectId && !!formId,
  });
  const strataChoices = strataQ.data?.find((v) => v.name === strataVar)?.choices || null;

  const run = async () => {
    if (!projectId || !formId || !strataVar) return;
    // Convertir population a números.
    const population: Record<string, number> = {};
    for (const [k, v] of Object.entries(pop)) {
      const n = parseFloat(v);
      if (!isNaN(n) && n >= 0) population[k] = n;
    }
    if (Object.keys(population).length === 0) {
      setErr("Define al menos una categoría poblacional.");
      return;
    }
    setBusy(true); setErr(null);
    try {
      const r = await AnalysisAPI.poststratify({
        project_id: projectId, form_id: formId, strata_var: strataVar,
        population, target_var: targetVar || null,
      });
      setResult(r);
    } catch (e) { setErr(e instanceof Error ? e.message : "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <PageHeader
        title="Ponderación (post-estratificación)"
        subtitle="Ajusta la muestra a una distribución poblacional conocida (p.ej. género, distrito)."
      />

      <Card className="mb-4">
        <ProjectFormPicker
          projectId={projectId} formId={formId}
          onProject={(id) => { setProjectId(id); setFormId(null); setStrataVar(""); setPop({}); setResult(null); }}
          onForm={(id) => { setFormId(id); setStrataVar(""); setPop({}); setResult(null); }}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3">
          <div>
            <label className="etiqueta">Variable de estratificación (p.ej. género)</label>
            <div className="max-h-44 overflow-y-auto bg-white border border-grid rounded-md p-2">
              {(strataQ.data ?? []).filter((v) => (v.type || "").startsWith("select_")).map((v) => (
                <label key={v.name} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-plano rounded px-1">
                  <input type="radio" checked={strataVar === v.name} onChange={() => { setStrataVar(v.name); setPop({}); }} className="accent-marca" />
                  <span className="text-sm">{v.label || v.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="etiqueta">Variable a estimar (opcional)</label>
            <div className="max-h-44 overflow-y-auto bg-white border border-grid rounded-md p-2">
              {(strataQ.data ?? []).filter((v) => (v.type || "").startsWith("select_")).map((v) => (
                <label key={v.name} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-plano rounded px-1">
                  <input type="radio" checked={targetVar === v.name} onChange={() => setTargetVar(v.name)} className="accent-marca" />
                  <span className="text-sm">{v.label || v.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {strataChoices && (
          <div className="mt-3">
            <label className="etiqueta">
              Distribución poblacional (% o conteo; se normaliza)
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(strataChoices).map(([val, label]) => (
                <div key={val} className="flex items-center gap-2">
                  <span className="text-xs text-tinta-3 flex-1 truncate" title={label}>{label}</span>
                  <input
                    type="number"
                    placeholder="%"
                    value={pop[label] ?? ""}
                    onChange={(e) => setPop((p) => ({ ...p, [label]: e.target.value }))}
                    className="input w-20 sm:w-24 py-1"
                  />
                </div>
              ))}
            </div>
            <div className="text-[11px] text-tinta-3 mt-1">
              Tip: las casillas pueden ser porcentajes (50/30/20) o conteos (5000/3000/2000); se normalizan solos.
            </div>
          </div>
        )}

        <button
          onClick={run}
          disabled={busy || !strataVar}
          className="btn mt-3"
        >
          {busy ? <Spinner /> : "Calcular ponderación"}
        </button>
      </Card>

      {err && <ErrorBanner message={err} />}
      {result?.error && <ErrorBanner message={result.error} />}

      {result && !result.error && (
        <div className="space-y-4">
          <Card>
            <div className="text-sm text-tinta-2">
              n total: <span className="text-marca">{result.n_total}</span> ·
              n efectivo (Neff): <span className="text-marca">{result.effective_n?.toFixed(1)}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm mt-2">
                <thead className="text-tinta-3 border-b border-grid">
                  <tr>
                    <th className="py-2 pr-3 text-left">Estrato</th>
                    <th className="py-2 pr-3 text-right">Muestra %</th>
                    <th className="py-2 pr-3 text-right">Población %</th>
                    <th className="py-2 pr-3 text-right">Peso</th>
                  </tr>
                </thead>
                <tbody>
                  {result.weights && Object.entries(result.weights).map(([cat, w]) => (
                    <tr key={cat} className="border-b border-grid">
                      <td className="py-2 pr-3">{cat}</td>
                      <td className="py-2 pr-3 text-right">{w.pct_sample.toFixed(1)}%</td>
                      <td className="py-2 pr-3 text-right">{w.pct_population != null ? `${w.pct_population.toFixed(1)}%` : "—"}</td>
                      <td className="py-2 pr-3 text-right text-marca">{w.weight != null ? w.weight.toFixed(3) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {result.weighted_target && (
            <Card>
              <div className="text-sm font-medium text-marca mb-2">
                Estimación ponderada de: {result.weighted_target.variable}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-tinta-3 border-b border-grid">
                    <tr>
                      <th className="py-2 pr-3 text-left">Categoría</th>
                      <th className="py-2 pr-3 text-right">n</th>
                      <th className="py-2 pr-3 text-right">Sin ponderar</th>
                      <th className="py-2 pr-3 text-right">Ponderado</th>
                      <th className="py-2 pr-3 text-right">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(result.weighted_target.categories)
                      .sort((a, b) => b[1].weighted_pct - a[1].weighted_pct)
                      .map(([cat, st]) => {
                        const delta = st.weighted_pct - st.unweighted_pct;
                        return (
                          <tr key={cat} className="border-b border-grid">
                            <td className="py-2 pr-3">{cat}</td>
                            <td className="py-2 pr-3 text-right">{st.n}</td>
                            <td className="py-2 pr-3 text-right text-tinta-3">{st.unweighted_pct.toFixed(1)}%</td>
                            <td className="py-2 pr-3 text-right text-marca">{st.weighted_pct.toFixed(1)}%</td>
                            <td className={`py-2 pr-3 text-right text-xs ${delta > 0 ? "text-estado-ok" : delta < 0 ? "text-estado-critico" : "text-tinta-3"}`}>
                              {delta >= 0 ? "+" : ""}{delta.toFixed(1)}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
